import { takeEvery, put, select, all } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import apiFetcher from '../../tools/api-fetcher';
import queryStringify from '../../tools/query-stringify';
import handleError from '../../tools/error-helper';

import * as types from '../../actions/school-candidate/types';
import * as actions from '../../actions/school-candidate';
import { getApplicationSummary } from '../../actions/applications';

import { stateKeys as schoolCandidateReferencesStateKeys } from '../../constants/school-candidate-references';
import httpStatusCodes from '../../constants/http-status-codes';

function* reloadApplicationSummary(candidateId) {
  const applicationDetails = yield select(state => state.applicationDetails);
  const application = applicationDetails.get('applicationSummary').toJS();

  if (application.candidateId === candidateId) {
    yield put(getApplicationSummary(application.schoolId, application.vacancyId, application.applicationId));
  }
}

function* getSchoolCandidateReferencesSummaries({ schoolId, candidateId }) {
    const { data } = yield apiFetcher.auth().get(`schools/${schoolId}/candidates/${candidateId}/references/summary`);
    yield put(actions.setSchoolCandidateReferencesSummaries(data));
}

function* getSelectedSchoolCandidateReference({ schoolId, candidateId, candidateReferenceId }) {
    const state = yield select(state => state.schoolCandidateReferences);

    if(state) {
      const cachedReferences = state.get(schoolCandidateReferencesStateKeys.schoolCandidateReferences);
      const cachedCandidateReference = cachedReferences.get(candidateReferenceId);
      
      if(cachedCandidateReference) {
        yield put(actions.setSelectedSchoolCandidateReference(cachedCandidateReference));
        return;
      }
    }

    const { data } = yield apiFetcher.auth().get(`schools/${schoolId}/candidates/${candidateId}/references/${candidateReferenceId}`);

    yield put(actions.addSchoolCandidateReferenceToCache(candidateReferenceId, data));
    yield put(actions.setSelectedSchoolCandidateReference(data));
}

function functionCallLoadingDecorator(targetFunction, loadingKey){
 return function*(){
   yield put(actions.setLoading(true, loadingKey));

  try {
    yield targetFunction.apply(this, arguments);
  } catch (exception) {
    handleError(exception);
  }

    yield put(actions.setLoading(false, loadingKey));
  };
}

function takeEveryWithLoadingKey(type, targetFunction, loadingKey) {
  return takeEvery(type, functionCallLoadingDecorator(targetFunction, loadingKey));
}


function* getSchoolCandidateCommentList({ schoolId, candidateId }) {
  yield put(actions.setSchoolCandidateCommentListLoading(true));
  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/candidates/${candidateId}/comments`);
    yield put(actions.setSchoolCandidateCommentList(data));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setSchoolCandidateCommentListLoading(false));
}

function* createSchoolCandidateComment({ schoolId, candidateId, parentCommentId, text, important }) {
  yield put(actions.setSchoolCandidateCommentCreating(true));
  let body = {
    parentCommentId: parentCommentId,
    commentDraft: {
      text: text,
      important: important
    }
  }
   try {
    yield apiFetcher.auth().post(`/schools/${schoolId}/candidates/${candidateId}/comments`, body);

    yield put(actions.getSchoolCandidateCommentList(schoolId, candidateId));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setSchoolCandidateCommentCreating(false));
}

function* updateSchoolCandidateComment({ schoolId, candidateId, commentId, text, important }) {
  yield put(actions.setSchoolCandidateCommentUpdating(true));
  let body = {
    commentDraft: {
      text: text,
      important: important
    }
  }
   try {
    yield apiFetcher.auth().put(`/schools/${schoolId}/candidates/${candidateId}/comments/${commentId}`, body);

    yield put(actions.getSchoolCandidateCommentList(schoolId, candidateId));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setSchoolCandidateCommentUpdating(false));
}

function* deleteSchoolCandidateComment({ schoolId, candidateId, commentId }) {
  yield put(actions.setSchoolCandidateCommentDeleting(true));
   try {
    yield apiFetcher.auth().delete(`/schools/${schoolId}/candidates/${candidateId}/comments/${commentId}`);
    
    yield put(actions.getSchoolCandidateCommentList(schoolId, candidateId));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setSchoolCandidateCommentDeleting(false));
}

function* getSchoolCandidateProfileDetails(schoolId, candidateId, loadingAction, mainAction, infoTypeUrlPart) {
  yield put(loadingAction(true));
   try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/candidates/${candidateId}/profile/${infoTypeUrlPart}`);

    yield put(mainAction(data));
  } catch (exception) {
    handleError(exception);
  }
  yield put(loadingAction(false));
}

function* getSchoolCandidateHealth({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileHealthLoading,
    actions.setSchoolCandidateProfileHealth,
    'health');
}

function* getSchoolCandidateBiographicalInfo({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId,
    candidateId,
    actions.setSchoolCandidateProfileBiographicalInformationLoading,
    actions.setSchoolCandidateProfileBiographicalInformation,
    'biographical-information');
}

function* getSchoolCandidatePersonal({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfilePersonalLoading,
    actions.setSchoolCandidateProfilePersonal,
    'personal');
}

function* getSchoolCandidateContactInformation({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileContactInformationLoading,
    actions.setSchoolCandidateProfileContactInformation,
    'contact-information');
}

function* getSchoolCandidateSportsActivities({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileSportActivitiesLoading,
    actions.setSchoolCandidateProfileSportActivities,
    'sports-and-activities');
}

function* getSchoolCandidateSecondarySchools({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileSecondarySchoolsLoading,
    actions.setSchoolCandidateProfileSecondarySchools,
    'secondary-schools');
}

function* getSchoolCandidateUniversitiesColleges({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileUniversitiesCollegesLoading,
    actions.setSchoolCandidateProfileUniversitiesColleges,
    'higher-educations');
}

function* getSchoolCandidateHousingPreferences({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileHousingPreferencesLoading,
    actions.setSchoolCandidateProfileHousingPreferences,
    'housing-preferences');
}

function* getSchoolCandidateLanguages({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileLanguagesLoading,
    actions.setSchoolCandidateProfileLanguages,
    'languages');
}

function* getSchoolCandidateTeachingCredentials({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileTeachingCredentialsLoading,
    actions.setSchoolCandidateProfileTeachingCredentials,
    'teaching-credentials');
}

function* getSchoolCandidateTeachingExperience({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileTeachingExperienceLoading,
    actions.setSchoolCandidateProfileTeachingExperience,
    'teaching-experience');
}

function* getSchoolCandidateDocuments({ schoolId, candidateId }) {
  yield getSchoolCandidateProfileDetails(
    schoolId, 
    candidateId,
    actions.setSchoolCandidateProfileDocumentsLoading,
    actions.setSchoolCandidateProfileDocuments,
    'documents/summaries');
}

function* getSchoolCandidateApplicationHistory({ schoolId, candidateId, queryParams }) {
  yield put(actions.setSchoolCandidateApplicationHistoryLoading(true));
   try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/candidates/${candidateId}/applications?${queryStringify({...queryParams})}`);

    yield put(actions.setSchoolCandidateApplicationHistory(data));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setSchoolCandidateApplicationHistoryLoading(false));
}

function* addSchoolCandidateToFavorites({ schoolId, candidateId }) {
  yield put(actions.setSchoolCandidateAddingToFavorites(true));

  try {
    yield apiFetcher.auth().post(`schools/${schoolId}/candidates/favorite`, { candidateId });

    yield* reloadApplicationSummary(candidateId);

    yield toastr.success('Candidate was successfuly added to favorites.');
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch(status) {
        case httpStatusCodes.Conflict: {
          toastr.error('Candidate is already added to favorites', { timeOut: 0 });
          break;
        }

        default: {
          handleError(exception, 'Failed to add candidate to favorites.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setSchoolCandidateAddingToFavorites(false));
}

function* removeSchoolCandidateFromFavorites({ schoolId, candidateId }) {
  yield put(actions.setSchoolCandidateRemovingFromFavorites(true));

  try {
    yield apiFetcher.auth().delete(`schools/${schoolId}/candidates/favorite/${candidateId}`);

    yield* reloadApplicationSummary(candidateId);

    yield toastr.success('Candidate was successfuly removed from favorites.');
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setSchoolCandidateRemovingFromFavorites(false));
}

function* blockSchoolCandidate({ schoolId, blockCandidateRequest }) {
  yield put(actions.setSchoolCandidateBlocking(true));

  try {
    yield apiFetcher.auth().post(`schools/${schoolId}/candidates/blocked`, blockCandidateRequest);

    yield* reloadApplicationSummary(blockCandidateRequest.candidateId);

    yield toastr.success('Candidate was successfuly blocked.');
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch(status) {
        case httpStatusCodes.Conflict: {
          toastr.error('Candidate is already blocked.', { timeOut: 0 });
          break;
        }

        default: {
          handleError(exception, 'Failed to block candidate.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setSchoolCandidateBlocking(false));
}

function* unblockSchoolCandidate({ schoolId, candidateId }) {
  yield put(actions.setSchoolCandidateUnblocking(true));

  try {
    yield apiFetcher.auth().delete(`schools/${schoolId}/candidates/blocked/${candidateId}`);

    yield* reloadApplicationSummary(candidateId);

    yield toastr.success('Candidate was successfuly unblocked.');
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setSchoolCandidateUnblocking(false));
}

export default function* schoolCandidateSaga() {
  yield all([
    takeEveryWithLoadingKey(types.SCHOOL_CANDIDATE_GET_CANDIDATE_REFERENCES_SUMMARIES, getSchoolCandidateReferencesSummaries, schoolCandidateReferencesStateKeys.schoolCandidateReferencesLoading),
    takeEveryWithLoadingKey(types.SCHOOL_CANDIDATE_GET_SELECTED_CANDIDATE_REFERENCE, getSelectedSchoolCandidateReference, schoolCandidateReferencesStateKeys.schoolSelectedCandidateReferencesLoading),

    takeEvery(types.SCHOOL_CANDIDATE_GET_COMMENT_LIST, getSchoolCandidateCommentList),
    takeEvery(types.SCHOOL_CANDIDATE_CREATE_COMMENT, createSchoolCandidateComment),
    takeEvery(types.SCHOOL_CANDIDATE_DELETE_COMMENT, deleteSchoolCandidateComment),
    takeEvery(types.SCHOOL_CANDIDATE_UPDATE_COMMENT, updateSchoolCandidateComment),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_BIOGRAPHICAL_INFORMATION, getSchoolCandidateBiographicalInfo),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_CONTACT_INFORMATION, getSchoolCandidateContactInformation),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_HEALTH, getSchoolCandidateHealth),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_HOUSING_PREFERENCES, getSchoolCandidateHousingPreferences),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_LANGUAGES, getSchoolCandidateLanguages),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_PERSONAL, getSchoolCandidatePersonal),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_SECONDARY_SCHOOLS, getSchoolCandidateSecondarySchools),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_SPORT_ACTIVITIES, getSchoolCandidateSportsActivities),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_TEACHING_CREDENTIALS, getSchoolCandidateTeachingCredentials),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_TEACHING_EXPERIENCE, getSchoolCandidateTeachingExperience),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_UNIVERSITIES_COLLEGES, getSchoolCandidateUniversitiesColleges),
    takeEvery(types.SCHOOL_CANDIDATE_PROFILE_GET_DOCUMENTS, getSchoolCandidateDocuments),

    takeEvery(types.SCHOOL_CANDIDATE_GET_APPLICATION_HISTORY, getSchoolCandidateApplicationHistory),

    takeEvery(types.SCHOOL_CANDIDATE_ADD_TO_FAVORITES, addSchoolCandidateToFavorites),
    takeEvery(types.SCHOOL_CANDIDATE_REMOVE_FROM_FAVORITES, removeSchoolCandidateFromFavorites),
    takeEvery(types.SCHOOL_CANDIDATE_BLOCK, blockSchoolCandidate),
    takeEvery(types.SCHOOL_CANDIDATE_UNBLOCK, unblockSchoolCandidate),
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/school/school-candidate.js